import React from 'react';
import { httpsCallable } from 'firebase/functions';
import { signInWithCustomToken, signOut } from 'firebase/auth';
import { auth, functions } from '../firebase';
import './ImpersonationBar.css';

const ImpersonationBar = ({ isImpersonating, targetUser, onEndImpersonation }) => {
  const [isEnding, setIsEnding] = React.useState(false);

  const handleEndImpersonation = async () => {
    if (isEnding) return;

    setIsEnding(true);
    try {
      const endImpersonationFunc = httpsCallable(functions, 'endImpersonation');
      const result = await endImpersonationFunc();

      if (result.data.success) {
        // Sign in with the admin token to return to admin account
        await signInWithCustomToken(auth, result.data.adminToken);

        // Clear impersonation state
        localStorage.removeItem('impersonationData');

        // Notify parent component
        if (onEndImpersonation) {
          onEndImpersonation();
        }

        // Reload to refresh the UI
        window.location.reload();
      }
    } catch (error) {
      console.error('Error ending impersonation:', error);

      // If normal end fails, offer force logout
      const forceLogout = window.confirm(
        'Failed to end impersonation normally. Would you like to force logout instead?\n\n' +
        'This will sign you out completely and you\'ll need to login again as admin.'
      );

      if (forceLogout) {
        await handleForceLogout();
      }
    } finally {
      setIsEnding(false);
    }
  };

  const handleForceLogout = async () => {
    try {
      // Clear all impersonation data
      localStorage.removeItem('impersonationData');
      document.body.classList.remove('impersonating');

      // Sign out completely
      await signOut(auth);

      // Notify parent component
      if (onEndImpersonation) {
        onEndImpersonation();
      }

      // Reload to go back to login screen
      window.location.href = '/';
    } catch (error) {
      console.error('Error during force logout:', error);
      // Last resort - clear everything and reload
      localStorage.clear();
      sessionStorage.clear();
      window.location.href = '/';
    }
  };

  if (!isImpersonating) return null;

  return (
    <div className="impersonation-bar">
      <div className="impersonation-content">
        <div className="impersonation-info">
          <span className="impersonation-icon">👤</span>
          <span className="impersonation-text">
            Impersonating: <strong>{targetUser?.email || targetUser?.displayName || targetUser?.uid}</strong>
          </span>
          <span className="impersonation-warning">
            (Admin Mode - All actions are logged)
          </span>
        </div>
        <div className="impersonation-actions">
          <button
            className="end-impersonation-btn"
            onClick={handleEndImpersonation}
            disabled={isEnding}
          >
            {isEnding ? 'Ending...' : 'End Impersonation'}
          </button>
          <button
            className="force-logout-btn"
            onClick={handleForceLogout}
            disabled={isEnding}
            title="Force logout if normal end doesn't work"
          >
            Force Logout
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImpersonationBar;